import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import { useFrameSurface } from "../shared/use-frame-surface";
import { openExternalUrl } from "@/lib/open-external-url";
import { safeWebUrl } from "@/lib/safe-web-url";
import { FRAME_CONTENT_PADDING } from "@/components/shapes/shared/frame-content-padding";
import { useT } from "@/i18n";
import { searchFrameMessages } from "@/i18n/messages/frames-search";

interface SearchResult {
  title: string;
  snippet: string;
  /** Agent-written results can omit it (e.g. inbox items shown as results). */
  url?: string;
}

/** Agent-written content is untrusted: keep only well-formed results. */
function readResults(raw: unknown): SearchResult[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item: unknown) => {
    if (!item || typeof item !== "object") return [];
    const { title, snippet, url } = item as Record<string, unknown>;
    return [
      {
        title: typeof title === "string" ? title : "",
        snippet: typeof snippet === "string" ? snippet : "",
        url: typeof url === "string" ? url : undefined,
      },
    ];
  });
}

// ─── Favicon helpers ──────────────────────────────────────────────────────────

function domainFromUrl(url?: string): string {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
}

function faviconLetter(url?: string): string {
  return domainFromUrl(url)[0]?.toUpperCase() ?? "?";
}

// ─── Result card ──────────────────────────────────────────────────────────────

function ResultCard({
  result,
  index,
}: {
  result: SearchResult;
  index: number;
}) {
  const t = useT(searchFrameMessages);
  const domain = domainFromUrl(result.url);
  const safeUrl = safeWebUrl(result.url);

  return (
    <button
      type="button"
      className="w-full rounded-control p-2 text-left transition-colors hover:bg-[var(--color-interactive-hover)] disabled:cursor-default"
      disabled={!safeUrl}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (safeUrl) void openExternalUrl(safeUrl);
      }}
      aria-label={
        safeUrl ? t("openResult", { title: result.title }) : undefined
      }
      data-testid={`search-result-${index}`}
    >
      <div className="mb-1 flex items-center gap-1.5">
        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-control-sm bg-[var(--color-surface-selected)]">
          <span className="text-appkit-mini font-semibold leading-none text-secondary">
            {faviconLetter(result.url)}
          </span>
        </div>
        <span className="truncate text-appkit-small text-muted">{domain}</span>
      </div>

      <p className="mb-0.5 truncate text-appkit-caption font-semibold text-primary">
        {result.title}
      </p>

      {result.snippet && (
        <p className="line-clamp-2 text-appkit-small leading-relaxed text-muted">
          {result.snippet}
        </p>
      )}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function SearchFrame({ id, data, selected }: FrameProps) {
  const t = useT(searchFrameMessages);
  const frameSurface = useFrameSurface(id);
  const d = data;
  const toolInput = d.content?.input as Record<string, unknown> | undefined;
  const query =
    (d.content?.query as string) ??
    (toolInput?.pattern as string) ??
    (toolInput?.query as string) ??
    (d.content?.tool_name as string) ??
    "";
  const results = readResults(d.content?.results);

  return (
    <div
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="search-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none nodrag min-h-0 flex-1 overflow-auto",
          FRAME_CONTENT_PADDING,
          selected && "nowheel",
        )}
        data-frame-content="true"
      >
        {query && (
          <div className="mb-2 flex items-center gap-1.5 rounded-control px-2 py-1">
            <Icons.Search className="h-3 w-3 text-muted" />
            <span className="text-xs text-secondary">{query}</span>
          </div>
        )}

        {d.status === "streaming" && results.length === 0 ? (
          <div className="flex items-center gap-2 py-3">
            <Spinner className="h-3 w-3" />
            <span className="text-xs text-muted">{t("searching")}</span>
          </div>
        ) : results.length > 0 ? (
          <div className="flex flex-col gap-2">
            {results.map((result, idx) => (
              <ResultCard key={result.url || idx} result={result} index={idx} />
            ))}
          </div>
        ) : toolInput ? (
          <div className="rounded-control p-2">
            <p className="text-appkit-mini font-medium text-muted mb-1">
              {t("toolInput")}
            </p>
            <pre className="scrollbar-none rounded-control bg-surface p-2 font-mono text-xs text-secondary overflow-x-auto leading-relaxed">
              <code>{JSON.stringify(toolInput, null, 2)}</code>
            </pre>
          </div>
        ) : (
          <p className="py-2 text-center text-xs italic text-muted">
            {t("noResults")}
          </p>
        )}
      </div>
    </div>
  );
}
